import { ref, computed, watch } from 'vue'
import { consola } from 'consola'
import {
  ACCESS_TOKEN_MAX_AGE_SECONDS,
  ADMIN_REFRESH_TOKEN_MAX_AGE_SECONDS,
} from '~/shared/config'

const logger = consola.withTag('session-manager')
const TOKEN_REFRESH_THRESHOLD_MS = 1 * 60 * 1000
const LOGOUT_IN_PROGRESS_KEY = 'auth_logout_in_progress'

const secureStorageHelper = {
  async isNativePlatform(): Promise<boolean> {
    /* v8 ignore next */
    if (!process.client) return false
    try {
      const { Capacitor } = await import('@capacitor/core')
      return Capacitor.isNativePlatform()
    } catch {
      return false
    }
  },
  async set(key: string, value: string): Promise<void> {
    if (!(await this.isNativePlatform())) return
    try {
      const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
      await SecureStoragePlugin.set({ key, value })
    } catch (error) {
      logger.warn(`Ошибка сохранения ${key} в SecureStorage:`, error)
    }
  },
  async remove(key: string): Promise<void> {
    if (!(await this.isNativePlatform())) return
    try {
      const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
      /* v8 ignore next */
      await SecureStoragePlugin.remove({ key })
    } catch (error) {
      logger.warn(`Ошибка удаления ${key} из SecureStorage:`, error)
    }
  },
}

const tokenExpiryTimes = ref<{ accessToken: number | null; adminAccessToken: number | null }>({
  accessToken: null,
  adminAccessToken: null,
})

const authAccessToken = ref<string | null>(null)
const authRefreshToken = ref<string | null>(null)
const authSessionId = ref<string | null>(null)
let isInitialized = false

if (process.client && !isInitialized) {
  isInitialized = true
  authAccessToken.value = localStorage.getItem('authAccessToken')
  authRefreshToken.value = localStorage.getItem('authRefreshToken')
  authSessionId.value = localStorage.getItem('authSessionId')
  const storedExpiry = localStorage.getItem('authTokenExpiry')
  if (storedExpiry) tokenExpiryTimes.value.accessToken = Number.parseInt(storedExpiry)

  watch(authAccessToken, (v) => v ? localStorage.setItem('authAccessToken', v) : localStorage.removeItem('authAccessToken'))
  watch(authRefreshToken, (v) => v ? localStorage.setItem('authRefreshToken', v) : localStorage.removeItem('authRefreshToken'))
  watch(authSessionId, (v) => v ? localStorage.setItem('authSessionId', v) : localStorage.removeItem('authSessionId'))
}

export const useSessionManager = () => {
  const authAdminAccessToken = useCookie<string | null>('authAdminAccessToken', {
    path: '/',
    sameSite: 'lax',
    secure: process.env.NODE_ENV === 'production',
  })
  const authAdminRefreshToken = useCookie<string | null>('authAdminRefreshToken', {
    maxAge: ADMIN_REFRESH_TOKEN_MAX_AGE_SECONDS,
    path: '/',
    sameSite: 'lax',
    secure: process.env.NODE_ENV === 'production',
  })

  const hasActiveSession = computed(() => {
    if (authAccessToken.value || authSessionId.value || authRefreshToken.value) return true
    if (!process.client) return false
    if (localStorage.getItem(LOGOUT_IN_PROGRESS_KEY) === 'true') return false
    const hasStoredToken = !!localStorage.getItem('authAccessToken')
    const hasStoredRefreshToken = !!localStorage.getItem('authRefreshToken')
    const hasStoredSession = !!localStorage.getItem('authSessionId')
    if (hasStoredToken && !authAccessToken.value) authAccessToken.value = localStorage.getItem('authAccessToken')
    if (hasStoredRefreshToken && !authRefreshToken.value) authRefreshToken.value = localStorage.getItem('authRefreshToken')
    if (hasStoredSession && !authSessionId.value) authSessionId.value = localStorage.getItem('authSessionId')
    return hasStoredToken || hasStoredSession || hasStoredRefreshToken
  })

  const hasAdminSession = computed(() => !!(authAdminAccessToken.value || authAdminRefreshToken.value))

  const isAccessTokenExpired = (): boolean => {
    if (!tokenExpiryTimes.value.accessToken) return false
    return Date.now() >= tokenExpiryTimes.value.accessToken
  }

  const shouldRefreshToken = (): boolean => {
    if (!tokenExpiryTimes.value.accessToken) return false
    if (!authAccessToken.value && !authRefreshToken.value && !authSessionId.value) return false
    return (tokenExpiryTimes.value.accessToken - Date.now()) <= TOKEN_REFRESH_THRESHOLD_MS
  }

  const getTokenRemainingTime = (): number => {
    if (!tokenExpiryTimes.value.accessToken) return Infinity
    if (!authAccessToken.value && !authRefreshToken.value && !authSessionId.value) return Infinity
    return Math.floor(Math.max(0, tokenExpiryTimes.value.accessToken - Date.now()) / 1000)
  }

  const setSession = (accessToken: string, sessionId: string, refreshToken?: string): void => {
    authAccessToken.value = accessToken
    authSessionId.value = sessionId
    if (refreshToken) authRefreshToken.value = refreshToken
    const expiryTime = Date.now() + (ACCESS_TOKEN_MAX_AGE_SECONDS * 1000)
    tokenExpiryTimes.value.accessToken = expiryTime
    if (process.client) localStorage.setItem('authTokenExpiry', expiryTime.toString())
  }

  const clearSession = (): void => {
    authAccessToken.value = null
    authRefreshToken.value = null
    authSessionId.value = null
    tokenExpiryTimes.value.accessToken = null
    if (process.client) localStorage.removeItem('authTokenExpiry')
  }

  const restoreSessionFromStorage = (): boolean => {
    if (!process.client) return false
    return !!(authAccessToken.value && authSessionId.value)
  }

  const setAdminTokens = (accessToken: string, refreshToken: string): void => {
    authAdminAccessToken.value = accessToken
    authAdminRefreshToken.value = refreshToken
    if (process.client) {
      ;(async () => {
        await secureStorageHelper.set('authAdminAccessToken', accessToken)
        await secureStorageHelper.set('authAdminRefreshToken', refreshToken)
      })()
    }
  }

  const clearAdminTokens = (): void => {
    authAdminAccessToken.value = null
    authAdminRefreshToken.value = null
    if (process.client) {
      ;(async () => {
        await secureStorageHelper.remove('authAdminAccessToken')
        await secureStorageHelper.remove('authAdminRefreshToken')
      })()
    }
  }

  return {
    authAccessToken,
    authRefreshToken,
    authSessionId,
    authAdminAccessToken,
    authAdminRefreshToken,
    hasActiveSession,
    hasAdminSession,
    setSession,
    clearSession,
    restoreSessionFromStorage,
    setAdminTokens,
    clearAdminTokens,
    isAccessTokenExpired,
    shouldRefreshToken,
    getTokenRemainingTime,
  }
}
